// -- Steps of uploading file

// 1. open bucket - openFileStream
// 2. create chunks - createChunk
// 3. get blob - getChunk
// 4. upload chunks - uploadChunk
// 5. close bucket - closeFileStream

import store from "/WebInterface/new-ui/upload/js/reducer.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import {
  openFile,
  closeFile,
  makeDir,
  uploadChunkXHR,
  checkFileExist,
  isFileOpen,
  getAltDomainToken,
} from "/WebInterface/new-ui/upload/utils/apis.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  handleFileOpenError,
  handleCloseFileRetryCommon,
  startFileUploadCommon,
  failFileOpenCommon,
  handleFileOpenResponse,
  handleRetryUploading,
  updateTimeStatusOfUploadingFileCommon,
  pausingUpload,
  pauseUpload,
  resumeUpload,
  closeFileUsingBlockUnBlockUpload,
} from "/WebInterface/new-ui/upload/js/common.js";
import uploadingQueue from "/WebInterface/new-ui/upload/js/uploading-queue.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";

class XHRUploads {
  constructor({ maxChunkSize, maxRetries, altDomains }) {
    this.chunksInfo = {
      chunks: [],
      chunkPos: 0,
      lastChunk: false,
      totalChunksUploaded: 0,
      lastBytesUploaded: 0,
      bytesUploaded: 0,
      bytesSent: 0,
      elapsedTime: 0,
      intervalId: null,
      index: 0,
    };

    this.isPaused = false;
    this.maxRequestPerDomain = 8;
    this.maxRequest = 8;
    this.initChunkSize = 0.5;
    this.MB = 1048576;
    this.maxChunkSize = maxChunkSize || 10;
    this.maxRetries = maxRetries || 10;
    this.maxCloseFileRetries = 10;
    this.retryChunksQueue = [];
    this.xhrs = {};
    this.uploadStartTime = null;
    this.pauseLog = [];

    const getBaseUrl = (url) => {
      const urlObject = new URL(url);
      const pathSegments = urlObject.pathname
        .split("/")
        .filter((segment) => segment !== "");

      let basePath = "/";
      const webInterfaceIndex = pathSegments.indexOf("WebInterface");

      if (webInterfaceIndex !== -1) {
        // If "WebInterface" is found, join all segments before it
        basePath += pathSegments.slice(0, webInterfaceIndex).join("/");
      } else if (pathSegments.length > 0) {
        // If "WebInterface" is not found, use the first segment (if any)
        basePath += pathSegments[0];
      }

      const curDomainUrl = `${urlObject.origin}${basePath}`;
      return curDomainUrl.replace(/\/+$/, "");
    };

    const localDomain = getBaseUrl(window.location);

    this.altDomains = [localDomain, ...altDomains];

    if (altDomains.length > 0) {
      const addRequest = this.maxRequestPerDomain * altDomains.length;
      this.maxRequest += addRequest;

      const handleAltDomainToken = async () => {
        try {
          const resp = await getAltDomainToken();
          this.altDomainToken = resp;
        } catch (error) {}
      };

      const handleAltDomainTokenCall = handleAltDomainToken.bind(this);
      handleAltDomainTokenCall();
    }
  }

  // TODO : we will be going to add global variables as they are required

  // slicing file and returning actual chunk
  getChunk = (file, start, size) => {
    return file.slice(start, start + size);
  };

  // creating chunks object with start,size and status
  createChunks = (resumeLoc) => {
    const file = this.streamingFile;
    let resume = resumeLoc;
    let chunkSize = this.initChunkSize * this.MB;

    const numberOfChunks = Math.min(
      Math.ceil((file.size - this.chunksInfo.bytesUploaded) / chunkSize),
      this.maxRequest
    );

    const { totalChunksUploaded } = this.chunksInfo;

    if (file.size < chunkSize) {
      chunkSize = file.size;
    }

    let currentAltDomainIndex = 0;

    for (
      let index = totalChunksUploaded;
      index < numberOfChunks + totalChunksUploaded;
      index++
    ) {
      const chunkStart = resume || this.chunksInfo.chunkPos;
      const chunkEnd = chunkStart + chunkSize;
      const isLastChunk = chunkEnd > file.size;

      // For Alternate Domain
      let serverName = this.altDomains[currentAltDomainIndex];

      if (isLastChunk) {
        chunkSize = file.size - chunkStart;
        this.chunksInfo.lastChunk = true;
      }

      this.lastChunkSize = chunkSize;

      const chunk = {
        index,
        start: chunkStart,
        size: chunkSize,
        status: "init",
        retries: 0,
        serverName,
        isAlternateDomain: currentAltDomainIndex !== 0,
      };

      currentAltDomainIndex++;

      if (currentAltDomainIndex === this.altDomains.length) {
        currentAltDomainIndex = 0;
      }

      this.chunksInfo.chunks.push(chunk);

      if (resume) resume = resume + chunkSize;
      this.chunksInfo.chunkPos = resume ? resume : chunkEnd;
    }

    return this.chunksInfo.chunks.slice(-numberOfChunks);
  };

  // Calculates chunk size
  getChunkSize = (timeTakenToUploadLastChunk) => {
    const file = this.streamingFile;
    const chunkPos = this.chunksInfo.chunkPos;

    let chunkSize = this.lastChunkSize;

    // If time taken to upload the chunk is less than 10s double the chunk size else half the chunk size
    if (timeTakenToUploadLastChunk <= 10000)
      chunkSize = this.lastChunkSize = Math.min(
        chunkSize * 2,
        this.maxChunkSize * this.MB
      );
    else
      chunkSize = this.lastChunkSize = Math.max(
        chunkSize / 2,
        this.initChunkSize * this.MB
      );
    //

    // Sets the size of last chunk according to file size
    if (chunkPos + chunkSize >= file.size) {
      chunkSize = file.size - chunkPos;
      this.chunksInfo.lastChunk = true;
    }

    return chunkSize;
  };

  // after successfully uploading of a chunk next chunk object will create using this function
  nextChunk = (timeTakenToUploadLastChunk, prevChunk) => {
    const { serverName, isAlternateDomain } = prevChunk;

    const chunkSize = this.getChunkSize(timeTakenToUploadLastChunk);
    const chunkIndex = this.chunksInfo.chunks.length;
    const chunkPos = this.chunksInfo.chunkPos;

    const chunk = {
      index: chunkIndex,
      start: chunkPos,
      size: chunkSize,
      status: "init",
      retries: 0,
      serverName,
      isAlternateDomain,
    };

    this.chunksInfo.chunks.push(chunk);

    this.chunksInfo.chunkPos += chunkSize;

    this.uploadChunk(chunk);
  };

  // TODO : we have to add callbacks for successful response and error response
  // uploading chunks
  uploadChunk = (chunk) => {
    const file = this.streamingFile;
    const fileInfo = this.streamingFileInfo;
    const { id: fileId, reUploadId, size } = fileInfo;

    let chunkUploadEndTime;
    let chunkUploadStartTime;

    let fileData = new FormData();

    // Construct the dynamic URL with fileId and chunk info
    const {
      index: chunkIndex,
      size: chunkSize,
      start: chunkStart,
      isAlternateDomain,
      serverName,
    } = chunk;

    let url = `${serverName}/U/${reUploadId || fileId}~`;

    if (isAlternateDomain) {
      url += `${this.altDomainToken}`;
    } else {
      url += `${chunkIndex + 1}~${chunkSize}`;
    }

    // Get the chunk data using the dynamic URL
    const chunkData = this.getChunk(file, chunkStart, chunkSize);
    this.chunksInfo.bytesSent += chunkSize;

    fileData.append("CFCD", chunkData, file.name);

    // Before xhr send

    const beforeXhrSend = (xhr) => {
      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.LOADING) {
          // Request completed
          if (
            xhr.status === 404 &&
            xhr.statusText.toLowerCase().indexOf("quota") >= 0 &&
            xhr.statusText.toLowerCase().indexOf("exceeded") >= 0
          ) {
            Object.values(this.xhrs).forEach((xhr) => {
              xhr.abort();
            });

            if (this.xhrs.length > 0) return;
            // Successful response
            clearInterval(this.chunksInfo.intervalId);
            handleFileOpenError(fileInfo, t("failedStorageQuotaExceeded"));
            this.invoke("growl", [
              t("yourFileQuotaExceeded"),
              t("fileUploadingFailed"),
              4000,
              true,
            ]);
            this.stopFileUploadingQueueOnQuotaStorageExceeded();
            console.log("Success:", xhr.responseText);
          }
        }
      };

      this.xhrs[chunkIndex] = xhr;

      if (isAlternateDomain)
        xhr.setRequestHeader(
          "X-TRANSFERSEGMENT",
          `${chunkIndex + 1}~${chunkSize}`
        );
    };

    // chunk upload load event
    const xhrLoadEvent = (evt, xhr) => {
      if (
        this.streamingFileInfo.id !== fileId ||
        uploadingQueue.queue.length === 0
      ) {
        return;
      }

      const response = xhr.response || xhr.responseText;

      if (response) {
        response = response.toString();
        if (response.toLowerCase().indexOf("error:") >= 0) {
          xhrErrorEvent();
        }
      }

      this.chunksInfo.totalChunksUploaded++;
      chunkUploadEndTime = Date.now();

      this.chunksInfo.bytesUploaded += chunkSize;
      this.chunksInfo.chunks[chunkIndex]["status"] = "done";

      delete this.xhrs[chunkIndex];

      store.dispatch("updateFileUploadStatus", {
        fileId,
        size: this.chunksInfo.bytesUploaded,
        maxSize: size,
        chunkSize: chunkSize,
      });

      if (this.chunksInfo.bytesUploaded >= size) {
        this.closeFileStream(fileInfo);
        return;
      }

      if (this.retryChunksQueue.length > 0) {
        this.retryChunksQueue = this.retryChunksQueue.filter(
          ({ index }) => index !== chunkIndex
        );

        if (this.retryChunksQueue.length === 0) {
          // Upload the next set of chunks
          this.uploadTheNextSetOfChunk();
        }
        return;
      }

      if (this.isPaused) {
        if (
          this.chunksInfo.totalChunksUploaded === this.chunksInfo.chunks.length
        ) {
          setTimeout(() => {
            this.pause();
          }, 1000);
        }
        return;
      }

      if (this.retrying) return;

      if (
        !this.chunksInfo.lastChunk &&
        this.chunksInfo.chunks.length >= this.maxRequest
      ) {
        const timeTakenToUploadLastChunk =
          chunkUploadEndTime - chunkUploadStartTime;

        this.nextChunk(timeTakenToUploadLastChunk, chunk);
        return;
      }
    };

    // chunk upload abort event
    const xhrAbortEvent = (evt) => {
      console.log("abort", evt);
      delete this.xhrs[chunkIndex];
    };

    // chunk upload error event
    const xhrErrorEvent = (evt, xhr) => {
      const { index, start, size, status, retries } = chunk;

      if (
        this.streamingFileInfo.id !== fileId ||
        uploadingQueue.queue.length === 0
      ) {
        return;
      }

      const doIndexPresent = this.retryChunksQueue.find(
        (c) => c.index === index
      );
      if (!doIndexPresent) this.retryChunksQueue.push({ ...chunk, xhr });

      const retryUpload = async () => {
        try {
          const response = await isFileOpen({
            upload_id: reUploadId || fileId,
            random: Math.random(),
            chunkInfo: JSON.stringify({
              index,
              start,
              size,
              status,
              uploaded: 0,
            }),
          });

          if (
            response === "CLOSED" ||
            response.toLowerCase().indexOf("error") >= 0 ||
            response.toLowerCase().indexOf("failure") >= 0
          ) {
            this.closeFileUsingBlockUnBlock(false, null, {
              status: "failed",
              uploadingError: t("failedFileClosed"),
            });
            return;
          }

          this.updateTimeStatusOfUploadingFile();

          this.retryChunksQueue.forEach((chunk) => {
            this.uploadChunk({ ...chunk, retries: retries + 1 });
          });
          this.retryChunksQueue = [];
        } catch (error) {
          this.closeFileUsingBlockUnBlock(false, null, {
            status: "failed",
            uploadingError: t("failedFileClosed"),
          });
        }
      };

      this.handleRetry(retries, retryUpload);
    };

    const loadEvent = xhrLoadEvent.bind(this);
    const abortEvent = xhrAbortEvent.bind(this);
    const errorEvent = xhrErrorEvent.bind(this);

    uploadChunkXHR(
      url,
      { loadEvent, abortEvent, errorEvent, beforeXhrSend },
      fileData
    );
    chunkUploadStartTime = Date.now();
  };

  handleRetry = async (retries, cb) => {
    const callback = () => {
      Object.values(this.xhrs).forEach((xhr) => {
        xhr.abort();
      });

      this.retryChunksQueue = [];
    };

    handleRetryUploading(this, retries, cb, callback);
  };

  updateTimeStatusOfUploadingFile = (resumeLoc = 0) => {
    updateTimeStatusOfUploadingFileCommon(resumeLoc, this);
  };

  nextFileInQueue = (canceled) => {
    uploadingQueue.nextFileInQueue(canceled);
  };

  stopFileUploadingQueueOnQuotaStorageExceeded = () => {
    clearInterval(this.chunksInfo.intervalId);
    uploadingQueue.stopFileUploadingQueueOnQuotaStorageExceeded();
  };

  invoke = (method, params) => {
    if (parent.window[method]) {
      return parent.window[method].apply(this, params);
    }
  };

  uploadDir = async (nameF, isDir = false) => {
    let folder = nameF.split("/");

    if (folder.length < 3 && !isDir) {
      return;
    }

    folder.pop();
    folder = folder.join("/") + "/";
    folder = utils.encodeURILocal(folder);

    try {
      await makeDir({ path: folder });
    } catch (error) {
      console.log("Error occurred during makeDir:", error);
      throw error;
    }
  };

  checkFilePartiallyUploaded = async (file) => {
    const { nameF, resumed } = file;

    if (!resumed) {
      return 0;
    }

    try {
      const resp = await checkFileExist({
        path: nameF,
        random: Math.random(),
      });
      return resp;
    } catch (error) {
      console.log("Error occurred during checkFileExist:", error);
      throw error;
    }
  };

  beforeUnloadEvent = (event) => {
    event.returnValue = "";
  };

  failFileOpen = (file, response) => {
    failFileOpenCommon(this, file, response);
  };

  startFileUpload = (file, resumeLoc) => {
    startFileUploadCommon(this, file, resumeLoc);
  };

  // TODO : we have to add callbacks for successful response and error response
  // open file stream - creating bucket to upload chunks
  openFileStream = async (file) => {
    this.chunksInfo = {
      chunks: [],
      chunkPos: 0,
      lastChunk: false,
      totalChunksUploaded: 0,
      lastBytesUploaded: 0,
      bytesSent: 0,
      bytesUploaded: 0,
      elapsedTime: 0,
      intervalId: null,
      index: 0,
    };

    this.retryChunksQueue = [];
    this.xhrs = {};
    this.isPaused = false;
    this.fileClosed = false;
    this.closeFileRetries = 0;
    if (!this.uploadStartTime) {
      this.uploadStartTime = Date.now();
    }
    this.pauseLog = [];

    const { id: fileId, nameF, size, isDir, file: fileToUpload } = file;

    this.uploadingStartedAt = Date.now();

    this.streamingFileInfo = file;
    this.streamingFile = fileToUpload;

    if (isDir) {
      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...file,
          status: "uploading",
        },
      });

      try {
        try {
          await this.uploadDir(nameF, isDir);
        } catch (err) {
          console.error("Failed to upload directory:", err);
          return; // Stop further execution
        }

        this.uploadingEndedAt = Date.now();

        store.dispatch("updateFileStatusAndRerenderFile", {
          [file.id]: {
            ...file,
            status: "done",
            time: this.uploadingEndedAt - this.uploadingStartedAt,
          },
        });

        this.nextFileInQueue();
      } catch (error) {
        console.log(error);
      }

      return;
    }

    try {
      const resp = await this.checkFilePartiallyUploaded(file);
      let resumeLoc = 0;
      if (!resp) {
        resumeLoc = 0;
      } else {
        let stats = resp.split(" ");
        stats = stats.filter((item) => item !== "");
        resumeLoc = parseInt(stats[4]);
        if (resumeLoc >= file.size) {
          resumeLoc = 0;
        }
        this.chunksInfo.bytesUploaded = resumeLoc;
      }

      try {
        await this.uploadDir(nameF);
      } catch (err) {
        console.error("Failed to upload directory:", err);
        return; // Stop further execution
      }
      const customFormData = this.streamingFileInfo?.customFormData || {};
      const formValues = store?.customCommonUploadFormValues || {};
      const metaUserInfo = this.invoke("getMetaUserInfo");
      const response = await openFile({
        ...formValues,
        ...customFormData,
        ...metaUserInfo,
        upload_path: nameF,
        upload_size: size,
        upload_id: file?.reUploadId || fileId,
        start_resume_loc: resumeLoc,
      });

      handleFileOpenResponse(this, file, response, resumeLoc);
    } catch (error) {
      console.log(error);
      handleFileOpenError(file, error);
    }
  };

  // TODO : we have to add callbacks for successful response and error response
  // closing bucket of chunks
  closeFileStream = async (file) => {
    const { id: fileId, nameF, size, lastModified, reUploadId } = file;
    const fileInfo = this.streamingFileInfo;

    if (this.fileClosed) return;
    this.fileClosed = true;

    const finalizingTimeoutId = setTimeout(() => {
      const updatedFileToCanceling = {
        ...fileInfo,
        status: "finalizing",
        finalizingMsg: "Finalizing",
      };

      store.dispatch("updateFileStatusAndRerenderFile", {
        [fileId]: updatedFileToCanceling,
      });

      store.dispatch("updateFileTimeStatus", {
        fileId,
        speed: 0,
        elapsedTime: this.chunksInfo.elapsedTime,
      });

      store.dispatch("updateFileUploadStatus", {
        fileId,
        size: this.chunksInfo.bytesUploaded,
        maxSize: size,
      });
    }, 3000);

    try {
      const response = await closeFile({
        upload_id: reUploadId || fileId,
        total_chunks: this.chunksInfo.chunks.length,
        total_bytes: size,
        filePath: nameF,
        lastModified: lastModified,
      });

      const decodedResponse =
        typeof response === "string" ? decodeURIComponent(response) : response;

      const { shouldRetry } = await this.handleCloseFileResponse(
        decodedResponse,
        file,
        fileInfo,
        finalizingTimeoutId
      );

      if (shouldRetry) {
        const retryCloseFile = async () => {
          try {
            const retryResponse = await closeFile({
              upload_id: reUploadId || fileId,
              total_chunks: this.chunksInfo.chunks.length,
              total_bytes: size,
              filePath: nameF,
              lastModified: lastModified,
            });

            const decodedRetryResponse =
              typeof retryResponse === "string"
                ? decodeURIComponent(retryResponse)
                : retryResponse;

            const { shouldRetry: shouldRetryAgain } =
              await this.handleCloseFileResponse(
                decodedRetryResponse,
                file,
                fileInfo,
                finalizingTimeoutId
              );

            if (shouldRetryAgain) {
              setTimeout(retryCloseFile, 2000);
            }
          } catch (err) {
            clearTimeout(finalizingTimeoutId);
            clearInterval(this.chunkRetryInterval);
            this.handleCloseFileRetry();
            this.invoke("growl", [
              "",
              t("somethingWentWrongWhileClosingFile"),
              5000,
              true,
            ]);
            C.error(`Upload:Error occurred during closeFile retry, ${err}`);
          }
        };

        setTimeout(retryCloseFile, 2000);
        return;
      }
    } catch (err) {
      clearTimeout(finalizingTimeoutId);
      clearInterval(this.chunkRetryInterval);
      this.handleCloseFileRetry();
      this.invoke("growl", [
        "",
        t("somethingWentWrongWhileClosingFile"),
        5000,
        true,
      ]);
      C.error(`Upload:Error occurred during closeFile, ${err}`);
    }
  };

  handleCloseFileResponse = async (
    response,
    file,
    fileInfo,
    finalizingTimeoutId
  ) => {
    if (response.startsWith("Decompressing")) {
      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...fileInfo,
          status: "finalizing",
        },
      });
      return { shouldRetry: true };
    } else if (response.startsWith("Finalizing")) {
      const pendingBytesMatch = response.match(/Pending bytes: (\d+)/);
      let finalizingMsg = "Finalizing";

      if (pendingBytesMatch && pendingBytesMatch[1]) {
        const pendingBytes = parseInt(pendingBytesMatch[1]);
        const totalBytes = file.size;
        const pendingFormatted = utils.formatBytes(pendingBytes);
        const totalFormatted = utils.formatBytes(totalBytes);
        finalizingMsg = `Finalizing (Pending: ${pendingFormatted}/${totalFormatted})`;
      }

      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...fileInfo,
          status: "finalizing",
          finalizingMsg: finalizingMsg,
        },
      });
      return { shouldRetry: true };
    } else if (response.indexOf("ERROR:") >= 0) {
      if (
        response.toLowerCase().indexOf("quota") >= 0 &&
        response.toLowerCase().indexOf("exceeded") >= 0
      ) {
        clearInterval(this.chunksInfo.intervalId);
        C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);
        handleFileOpenError(fileInfo, response);
        this.invoke("growl", [response, t("fileClosingFailed"), 4000, true]);
        this.nextFileInQueue(true);
        return { shouldRetry: false };
      }
      this.handleCloseFileRetry(response);
      return { shouldRetry: false };
    } else {
      clearTimeout(finalizingTimeoutId);
      removeEventListener("beforeunload", this.beforeUnloadEvent);
      this.uploadingEndedAt = Date.now();

      const updatedFile = {
        ...file,
        status: "done",
        time: this.uploadingEndedAt - this.uploadingStartedAt,
      };

      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: updatedFile,
      });

      clearInterval(this.chunksInfo.intervalId);
      C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);

      this.nextFileInQueue();
      return { shouldRetry: false };
    }
  };

  handleCloseFileRetry = async (err) => {
    await handleCloseFileRetryCommon(this, err);
  };

  pausing = () => {
    pausingUpload(this);
  };

  pause = () => {
    pauseUpload(this);
  };

  resume = () => {
    resumeUpload(this, this.uploadTheNextSetOfChunk);
  };

  cancel = async () => {
    this.closeFileUsingBlockUnBlock();
  };

  closeFileUsingBlockUnBlock = (
    preStatus = {
      status: "canceling",
    },
    postStatus = {
      status: "canceled",
    }
  ) => {
    const beforeClosingFile = () => {
      // Clear the interval responsible for chunk upload
      clearInterval(this.chunksInfo.intervalId);
      C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);
      Object.values(this.xhrs).forEach((xhr) => {
        xhr.abort();
      });
    };

    closeFileUsingBlockUnBlockUpload(
      preStatus,
      postStatus,
      beforeClosingFile,
      this
    );
  };

  uploadTheNextSetOfChunk = (resumeLoc) => {
    const chunks = this.createChunks(resumeLoc);

    chunks.forEach((chunk) => {
      this.uploadChunk(chunk);
    });

    this.handleUpdateUploadingStatus(true);
  };

  handleUpdateUploadingStatus = (uploading) => {
    uploadingQueue.handleUpdateUploadingStatus(uploading);
  };
}

export default XHRUploads;
